import { useEffect } from 'react'

export const SITE = 'https://wiki.sighthub.cloud'
const SITE_NAME = 'SightHub Wiki'

export interface Meta {
  title: string
  description?: string
  /** Path only, e.g. `/d/12`. Joined to SITE for the canonical URL. */
  path: string
  /**
   * Keep it out of the index. Anything staff-only, anything transient (search
   * results, editor, admin), and every page whose content we do not want ranked.
   */
  noindex?: boolean
  tags?: string[]
  updatedAt?: string
  author?: string
}

const upsert = (selector: string, create: () => HTMLElement): HTMLElement => {
  const found = document.head.querySelector(selector)
  if (found) return found as HTMLElement
  const el = create()
  document.head.appendChild(el)
  return el
}

const meta = (attr: 'name' | 'property', key: string, content: string) => {
  const el = upsert(`meta[${attr}="${key}"]`, () => {
    const m = document.createElement('meta')
    m.setAttribute(attr, key)
    return m
  })
  el.setAttribute('content', content)
}

const link = (rel: string, href: string) => {
  const el = upsert(`link[rel="${rel}"]`, () => {
    const l = document.createElement('link')
    l.setAttribute('rel', rel)
    return l
  })
  el.setAttribute('href', href)
}

const JSONLD_ID = 'sh-jsonld'

/**
 * Set the document's metadata for the current route.
 *
 * These tags are written by JavaScript, so a crawler only sees them after it
 * renders the page. Google does; most others do not. Real coverage needs the
 * public docs pre-rendered to static HTML at build time — see scripts/gen-seo.mjs.
 */
export function useMeta(m: Meta | null) {
  useEffect(() => {
    if (!m) return

    const url = `${SITE}${m.path}`
    const title = m.path === '/' ? SITE_NAME : `${m.title} · ${SITE_NAME}`
    const description = m.description?.trim() || 'Reference documentation for every SightHub service.'

    document.title = title
    meta('name', 'description', description)

    // An internal page must never be indexed, and neither must a search result or
    // an editor screen. `noarchive` also keeps the snippet out of the cache.
    meta(
      'name',
      'robots',
      m.noindex ? 'noindex, nofollow, noarchive' : 'index, follow, max-image-preview:large',
    )

    link('canonical', url)

    meta('property', 'og:type', m.path.startsWith('/d/') ? 'article' : 'website')
    meta('property', 'og:site_name', SITE_NAME)
    meta('property', 'og:title', m.title)
    meta('property', 'og:description', description)
    meta('property', 'og:url', url)
    meta('property', 'og:image', `${SITE}/sighthub-logo.png`)
    meta('name', 'twitter:card', 'summary')
    meta('name', 'twitter:title', m.title)
    meta('name', 'twitter:description', description)

    if (m.tags?.length) meta('name', 'keywords', m.tags.join(', '))

    // Structured data, but never for a page we are telling crawlers to ignore.
    document.getElementById(JSONLD_ID)?.remove()
    if (!m.noindex && m.path.startsWith('/d/')) {
      const script = document.createElement('script')
      script.id = JSONLD_ID
      script.type = 'application/ld+json'
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'TechArticle',
        headline: m.title,
        description,
        url,
        ...(m.updatedAt ? { dateModified: m.updatedAt } : {}),
        ...(m.author ? { author: { '@type': 'Person', name: m.author } } : {}),
        ...(m.tags?.length ? { keywords: m.tags.join(', ') } : {}),
        publisher: { '@type': 'Organization', name: 'SightHub' },
      })
      document.head.appendChild(script)
    }
  }, [m?.title, m?.description, m?.path, m?.noindex, m?.updatedAt, m?.author, m?.tags?.join(',')])
}
